import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Login() {
  const [email, setEmail] = useState('brand1@example.com');
  const [password, setPassword] = useState('ChangeMe123!');
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkExistingAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setChecking(false);
        return;
      }

      try {
        const response = await fetch('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const userData = await response.json();
          const user = userData.user;

          // User is already authenticated, redirect to appropriate page
          if (user.role === 'brand') {
            navigate(`/brand/${user.tenantId}/dashboard`, { replace: true });
          } else if (user.role === 'admin') {
            navigate('/admin/users', { replace: true });
          } else {
            navigate('/home', { replace: true });
          }
        } else {
          // Token is invalid, remove it
          localStorage.removeItem('token');
          setChecking(false);
        }
      } catch (error) {
        localStorage.removeItem('token');
        setChecking(false);
      }
    };

    checkExistingAuth();
  }, [navigate]);

  async function loginSession(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    setIsLoading(true);
    
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Invalid credentials');
      }
      const data = await res.json();
      // Store token in localStorage
      localStorage.setItem('token', data.accessToken);

      // Get user info to determine redirect
      const userResponse = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${data.accessToken}` }
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        const user = userData.user;
        
        // Redirect immediately based on user role
        if (user.role === 'brand') {
          navigate(`/brand/${user.tenantId}/dashboard`);
        } else if (user.role === 'admin') {
          navigate('/admin/users');
        } else {
          navigate('/home');
        }
      } else {
        navigate('/home');
      }
    } catch (err: any) {
      setIsLoading(false);
      setMessage(err.message || 'Login failed');
    }
  }

  // Show nothing while checking existing authentication
  if (checking) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm border rounded-lg p-6">
        <h1 className="text-2xl font-semibold mb-4">Sign in</h1>
        <form className="space-y-4" onSubmit={loginSession}>
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
        <div className="mt-3 text-right">
          <a href="/forgot" className="text-sm text-blue-600 hover:underline">Forgot password?</a>
        </div>
        {message && <p className="text-sm text-muted-foreground mt-4">{message}</p>}
      </div>
    </div>
  );
}